import { KEYCODE_MAP_HFD_LAYOUT } from "./keyCodeMapperHfd";
import { resolveHfdFnFuncDisplayKeyCode } from "./hfdFnDisplay";

/** matrix (row,col) → Fn 层展示用 keyCode（对齐 X87 V2 出厂 Fn 图 / 竞品驱动） */
const X87V2_FN_CELL_OVERRIDES: Readonly<Record<string, number>> = {
  // Row 0 — Esc
  "0,0": resolveHfdFnFuncDisplayKeyCode(4096, { v2: true }), // 恢复出厂设置
  // Row 1 — `~123…-= Backspace | Ins·Home·PgUp（矩阵 col 见 KEYCODE_MAP_HFD_LAYOUT.huafenda87）
  "1,13": resolveHfdFnFuncDisplayKeyCode(12294, { v2: true }), // Backspace 关闭灯光
  "1,15": resolveHfdFnFuncDisplayKeyCode(29, { v2: false }), // Home 正面灯条颜色切换
  // Row 2 — Tab / QWER … \| [ ] Del·End·PgDn
  "2,0": resolveHfdFnFuncDisplayKeyCode(93, { v2: false }), // Tab 一键白光
  "2,1": resolveHfdFnFuncDisplayKeyCode(8192, { v2: true }), // Q 蓝牙通道1
  "2,2": resolveHfdFnFuncDisplayKeyCode(8193, { v2: true }), // W 蓝牙通道2
  "2,3": resolveHfdFnFuncDisplayKeyCode(8194, { v2: true }), // E 蓝牙通道3
  "2,4": resolveHfdFnFuncDisplayKeyCode(8202, { v2: true }), // R 无线回连
  "2,13": resolveHfdFnFuncDisplayKeyCode(12288, { v2: true }), // \|（keycode 49）灯光效果切换
  "2,14": resolveHfdFnFuncDisplayKeyCode(12289, { v2: true }), // Delete 灯光颜色切换
  "2,15": resolveHfdFnFuncDisplayKeyCode(27, { v2: false }), // End 正面灯条效果切换
  // Row 4 — ↑
  "4,14": resolveHfdFnFuncDisplayKeyCode(12290, { v2: true }), // 灯光亮度+
  // Row 5 — 底行 / 方向键
  "5,1": resolveHfdFnFuncDisplayKeyCode(22, { v2: false }), // L-Win 锁定Win
  "5,10": resolveHfdFnFuncDisplayKeyCode(7, { v2: false }), // R-Alt 查询电量
  "5,14": resolveHfdFnFuncDisplayKeyCode(12293, { v2: true }), // ← 灯光速度-
  "5,15": resolveHfdFnFuncDisplayKeyCode(12291, { v2: true }), // ↓ 灯光亮度-
  "5,16": resolveHfdFnFuncDisplayKeyCode(12292, { v2: true }), // → 灯光速度+
};

const X87V2_BASE_MATRIX = KEYCODE_MAP_HFD_LAYOUT.huafenda87;

function readX87V2EquipmentFlag(): boolean {
  try {
    const raw = localStorage.getItem("equipmentData");
    if (!raw) return false;
    const eq = JSON.parse(raw) as {
      displayname?: string;
      productName?: string;
      keyboardType?: string;
    };
    const name = String(eq?.displayname ?? eq?.productName ?? "").trim();
    return name === "X87 V2" || String(eq?.keyboardType ?? "") === "huafenda87";
  } catch {
    return false;
  }
}

/** X87 V2 出厂 Fn 层展示矩阵：有映射的格显示 Fn 功能，其余保留物理键帽（F1–F12 等） */
export function buildHuafendaX87V2FnLayerKeyCodes(): number[][] {
  return X87V2_BASE_MATRIX.map((row, rowIndex) =>
    row.map((baseCell, col) => {
      const overrideKey = `${rowIndex},${col}`;
      if (
        Object.prototype.hasOwnProperty.call(
          X87V2_FN_CELL_OVERRIDES,
          overrideKey
        )
      ) {
        return X87V2_FN_CELL_OVERRIDES[overrideKey];
      }
      if (baseCell === 0) return 0;
      return baseCell;
    })
  );
}

/**
 * Fn 层展示：完全使用出厂静态表，不合并 SDK 解析结果。
 * SDK getFnKeyData 按 keyIndex 槽位存储，与矩阵列错位时会把功能贴到 F 区/错误键位（图1）。
 */
export function applyHuafendaX87V2FnLayerToSdkRows(
  rows: { layer: number; row: number; keyboardLayout: number[] }[]
): { layer: number; row: number; keyboardLayout: number[] }[] {
  const fnMatrix = buildHuafendaX87V2FnLayerKeyCodes();
  return rows.map((row) => {
    const staticRow = fnMatrix[row.row];
    if (!staticRow) return row;
    return { ...row, keyboardLayout: [...staticRow] };
  });
}

/** X87 V2（huafenda87）Fn 层使用静态出厂映射表 */
export function shouldApplyHuafendaX87V2FnLayer(
  keyboardType: string,
  layer: number
): boolean {
  if (layer === 0) return false;
  if (keyboardType === "huafenda87") return true;
  return readX87V2EquipmentFlag();
}

export function getHuafendaX87V2FnLayerRow(rowIndex: number): number[] | null {
  const matrix = buildHuafendaX87V2FnLayerKeyCodes();
  return matrix[rowIndex] ?? null;
}
